import { useCallback, useReducer, useEffect, useRef } from 'react';

import { StaticDataFetcherItem } from '@hh.ru/magritte-common-data-provider';
import { isArraysEquals } from '@hh.ru/magritte-ui-select/isArraysEquals';
import { UseSelectState } from '@hh.ru/magritte-ui-select/types';

const ADD = 'ADD';
const SET = 'SET';
const REMOVE = 'REMOVE';
const SINGLE_CHOICE = 'SINGLE_CHOICE';
const CLEAR = 'CLEAR';

const reducer = <DataType extends StaticDataFetcherItem>(
    state: DataType[],
    {
        type,
        payload,
    }:
        | { type: typeof ADD | typeof REMOVE | typeof SINGLE_CHOICE; payload: DataType[] }
        | { type: typeof CLEAR; payload?: DataType }
        | { type: typeof SET; payload: DataType[] }
) => {
    switch (type) {
        case ADD:
            return payload;
        case SET:
            return [...payload];
        case REMOVE:
            return payload;
        case CLEAR:
            return [];
        case SINGLE_CHOICE:
            return payload;
        default:
            return state;
    }
};

export const useSelectState = <
    DataType extends StaticDataFetcherItem,
    MultipleType extends boolean,
    ValueType = [MultipleType] extends [true] ? DataType[] : DataType,
>(
    multiple: boolean,
    value: DataType[] | DataType,
    showOptions: boolean,
    hasChangesButton?: boolean,
    onChangeExternalHandler?: (value: ValueType) => void,
    onSelectOption?: (values: DataType[]) => void
): UseSelectState<MultipleType, DataType, ValueType> => {
    const valuesArr = Array.isArray(value) ? value : [value];
    const prevValues = useRef(valuesArr);
    const initialValuesOnMount = useRef(valuesArr);
    const initialValuesOnOpening = useRef<DataType[]>([]);
    const [state, dispatch] = useReducer(reducer<DataType>, valuesArr);
    const stateRef = useRef(state);
    stateRef.current = state;

    // используем ref, чтобы не вызывать пересчетов функции внутри хука из-за стрелочных колбэков
    const onChangeExternalHandlerRef = useRef(onChangeExternalHandler);
    onChangeExternalHandlerRef.current = onChangeExternalHandler;
    const onSelectOptionRef = useRef(onSelectOption);
    onSelectOptionRef.current = onSelectOption;

    const updateSelectedValues = useCallback(
        (values: DataType[], needEmitChangeEvent = true) => {
            if (needEmitChangeEvent) {
                const hasUnsavedChanges = !isArraysEquals<string>(
                    prevValues.current.map((item) => item.value),
                    values.map((item) => item.value)
                );
                if (hasUnsavedChanges) {
                    onChangeExternalHandlerRef.current?.((multiple ? values : values[0]) as ValueType);
                }
            }
            prevValues.current = values;
        },
        [multiple]
    );

    useEffect(() => {
        if (showOptions) {
            initialValuesOnOpening.current = state;
        }
        // сохраняем values при открытии select
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [showOptions]);

    useEffect(() => {
        const newValues = Array.isArray(value) ? value : [value];
        if (isArraysEquals(newValues, stateRef.current)) {
            return;
        }
        // если values и редьюсер разошлись
        dispatch({ type: SET, payload: newValues });
        updateSelectedValues(newValues, false);
    }, [updateSelectedValues, value]);

    return {
        values: (multiple ? state : state[0]) as ValueType,
        clearSelectedValues: () => {
            dispatch({ type: CLEAR });
            updateSelectedValues([]);
        },
        setInitialValues: () => {
            dispatch({ type: SET, payload: initialValuesOnMount.current });
            updateSelectedValues(initialValuesOnMount.current);
        },
        setInitialValuesWithoutOnChange: () => {
            // в остальных кейсах селект обновляется сразу
            // либо выбранные значения сразу применяются
            if (!hasChangesButton) {
                return;
            }
            const hasInitialValuesAndPrevEmittedValusAreEqual = isArraysEquals<string>(
                initialValuesOnOpening.current.map((item) => item.value),
                prevValues.current.map((item) => item.value)
            );
            // если values после открытия не поменялись
            // то надо скоректировать состояния в drop/bottom-sheet
            // иначе в drop/bottom-sheet будут те же элементы что и при предыдущем открытии
            // даже если не нажимали кнопку применить
            if (hasInitialValuesAndPrevEmittedValusAreEqual) {
                dispatch({ type: SET, payload: initialValuesOnOpening.current });
            }
        },
        onChange: useCallback(
            (value, needToPut) => {
                const multipleActionType = needToPut ? ADD : REMOVE;
                const actionType = multiple ? multipleActionType : SINGLE_CHOICE;
                // single
                let newValue = [value];
                // multiple
                if (actionType === ADD) {
                    newValue = [...state, value];
                }
                if (actionType === REMOVE) {
                    newValue = state.filter((item) => item.value !== value.value);
                }
                dispatch({ type: actionType, payload: newValue });
                onSelectOptionRef.current?.(newValue);
                if (!hasChangesButton) {
                    updateSelectedValues(newValue);
                }
            },
            [hasChangesButton, multiple, state, updateSelectedValues]
        ),
        setSelectedValues: () => {
            updateSelectedValues(state);
        },
        initialValuesOnOpening,
    };
};
